import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';

import styles from './LineItemInfo.module.scss';
import { Price, Button, ShowMore } from '@components';
import { Typography, Icon, Counter } from 'gd-design-library';
import { LineItem } from '@services/cart';
import { getImageTransformationByProductType, getSuccessfullyLoadedUrls, pushRemoveFromCartEvent } from '@utils';
import NoImagePlaceholder from '@assets/images/no-image-placeholder.png';
import { Alert } from '@mui/material';
import { ImageTransformation, TRANSFORMATIONS, ProductService } from '@services';
import { useServiceRef } from 'hooks';
import Image from 'next/image';

const CN = 'line-item';
const productClassName = `${CN}--product`;
const infoSectionClassName = `${CN}--info-section`;
const nameClassName = `${infoSectionClassName}--name`;
const attributesClassName = `${infoSectionClassName}--attributes`;
const attributeClassName = `${attributesClassName}--attribute`;
const removeButtonClassName = `${infoSectionClassName}--remove-button`;
const itemControlRowClassName = `${infoSectionClassName}--item-control-row`;
const priceClassName = `${itemControlRowClassName}--price`;
const imageClassName = `${CN}--image`;
const outOfStockClassName = `${CN}--out-of-stock`;

const cx = classNames.bind(styles);

interface Props {
  lineItem: LineItem,
  onItemQuantityChange?: (lineItemId: string, newQuantity: number) => unknown,
  onLineItemRemove?: (lineItemId: string) => unknown,
  readOnly?: boolean,
  disabled?: boolean,
  className?: string,
  isOutOfStock?: boolean,
  availableQuantity?: number;
}

export const LineItemInfo: React.FC<Props> = ({
  lineItem,
  onItemQuantityChange,
  onLineItemRemove,
  disabled = false,
  readOnly = false,
  className = '',
  isOutOfStock = false,
  availableQuantity
}) => {
  const [ imageUrl, setImageUrl ] = useState('');
  const [ fetchedAvailableQuantity, setFetchedAvailableQuantity ] = useState<number | null>(null);

  const imageTransformation = new ImageTransformation();
  const productServiceRef = useServiceRef(ProductService);

  useEffect(() => {
    (async (): Promise<void> => {
      const [ loadedUrl ] = await getSuccessfullyLoadedUrls([ lineItem.variant?.imageUrl ?? '' ]);
      setImageUrl(loadedUrl
        ? imageTransformation.transform(
          loadedUrl,
          [ ...getImageTransformationByProductType(
            typeof lineItem.productType === 'string' ? lineItem.productType : (lineItem.productType?.name ?? 'clothing')
          ),
          {
            name: TRANSFORMATIONS.RESIZE,
            params: { width: 90, height: 90, crop: 'fill' }
          } ]
        )
        : NoImagePlaceholder.src
      );
    })();
  }, []);

  useEffect(() => {
    (async (): Promise<void> => {
      const sku = lineItem.variant?.sku;
      if (availableQuantity === undefined && sku) {
        const quantity = await productServiceRef.current.getAvailableQuantity(sku);
        setFetchedAvailableQuantity(quantity);
      } else {
        setFetchedAvailableQuantity(null);
      }
    })();
  }, [ availableQuantity, lineItem.variant?.sku ]);


  return (
    <div className={ cx(CN, className) }>
      {isOutOfStock && (
        <div className={ cx(outOfStockClassName) }>
          <Alert severity='error'>
            {availableQuantity !== 0 ? (
              <>
               Only {availableQuantity} {availableQuantity === 1 ? 'item is' : 'items are'} available,
               please update quantity or product will be removed from the cart on checkout.
              </>
            ) : (
              <>Product is out of stock, product will be removed from the cart on checkout.</>
            ) }
          </Alert>
        </div>
      )}
      <div className={ cx(productClassName) }>
        {imageUrl && (
          <Image
            alt={ lineItem.name }
            width={ 90 }
            height={ 90 }
            className={ cx(imageClassName) }
            src={ imageUrl }
            loading='lazy'
            sizes='90px'
          />
        )}
        <div className={ cx(infoSectionClassName) }>
          <Typography variant='h6' className={ cx(nameClassName) }>{ lineItem.name }</Typography>
          <div className={ cx(attributesClassName) }>
            {(lineItem.variant?.attributes ?? [])
              .filter((attr: { name: string }) => attr.name !== 'sokos-sokos_product-purchase_limit')
              .map((attr: { name: string; label: string; value: string }) => (
                <Typography key={ attr.name } className={ cx(attributeClassName) }>
                  {attr.label}:{' '}
                  <ShowMore numOfCharsToShow={ 100 }>{String(attr.value ?? '')}</ShowMore>
                </Typography>
              ))
            }
            {readOnly && (
              <Typography variant='h6'>
                {lineItem.quantity === 1 ? `${lineItem.quantity} item` : `${lineItem.quantity} items`}
              </Typography>
            )}
          </div>
          <div className={ cx(itemControlRowClassName) }>
            {!readOnly &&
            <>
              {onItemQuantityChange && (
                <Counter
                  initial={ lineItem.quantity }
                  min={ 1 }
                  max={ availableQuantity ?? fetchedAvailableQuantity }
                  onCounterChange={ (newQuantity): void => {
                    if (disabled || newQuantity === lineItem.quantity) return;
                    onItemQuantityChange(lineItem.id, newQuantity);
                  } }
                />
              )}
              <Button
                isIcon
                variant='inherit'
                color='secondary'
                onClick={ (): void => {
                  if (disabled) return;
                  onLineItemRemove?.(lineItem.id);
                  pushRemoveFromCartEvent(lineItem);
                } }
                className={ cx(removeButtonClassName) }
              >
                <Icon name='deleteOutlined' width={ 24 } height={ 24 }/>
              </Button>
            </>
            }
            <Price className={ cx(priceClassName) } price={ lineItem.totalPrice }></Price>
          </div>
        </div>
      </div>
    </div>
  );
};
